/**
 * Vrai nesting : placement par silhouette réelle (canal alpha) avec rotations libres.
 *
 * Moteur « skyline par profils de forme » :
 *  - chaque logo est rastérisé (forme alpha ou rectangle plein) sous plusieurs angles
 *    sur une grille fine ; on en tire, colonne par colonne, un profil haut/bas ;
 *  - la planche maintient une « skyline » (hauteur occupée par colonne) ;
 *  - un logo est posé à la position/angle qui minimise la hauteur totale, sa forme
 *    épousant la skyline (deux arcs s'emboîtent au lieu d'empiler leurs rectangles) ;
 *  - la marge est garantie en dilatant l'empreinte COMMISE (pas la forme testée) :
 *    l'écart forme-à-forme vaut toujours margeMm, et les bords de planche restent flush.
 *
 * Choix volontairement conservateur : une colonne est remplie de son point le plus haut
 * à son point le plus bas (pas de « tuck-under » sous un surplomb) — jamais de collision.
 *
 * Complément « poches » : quand un logo ne tient plus SUR la skyline d'une planche
 * (elle est pleine), on cherche un trou dans l'espace déjà passé (intervalles occupés
 * par colonne, dilatés de la marge). Un petit logo se glisse ainsi dans les vides
 * entre les grands au lieu d'allonger le film.
 */

import { DTFItem, PackResult, Placement, Planche } from './packing';
import { DTFSettings } from './settings';
import { getShapeMask, ShapeMask } from './shape';

/** Résolution de la grille : 4 cellules par cm (2,5 mm) */
const RES = 4;
const CELL = 1 / RES;
const ANGLE_STEP_FINE = 15;
const ANGLE_STEP_COARSE = 45;
/** Au-delà de ce nombre d'instances, angles réduits (perf) */
const MANY_INSTANCES = 40;
/** Alpha minimal (0-255) pour considérer une cellule couverte après rotation.
 * 1 (et pas plus) : à RES=4 une cellule fait 2,5 mm — un trait fin anti-aliasé
 * peut n'y laisser qu'un alpha très faible ; l'ignorer ferait « disparaître »
 * le trait du profil et deux logos pourraient se superposer. */
const COVER_MIN = 1;

const round1 = (v: number) => Math.round(v * 10) / 10;
const rad = (deg: number) => (deg * Math.PI) / 180;

interface Profile {
	/** largeur du bbox pivoté, en cellules */
	c: number;
	/** profil haut par colonne (cellules depuis le haut du bbox), -1 = colonne vide */
	top: Int32Array;
	/** profil bas BRUT par colonne (exclusif), -1 = colonne vide */
	bot: Int32Array;
	/** profil bas DILATÉ de la marge, indexé sur [x - r, x + c + r), -1 = vide */
	botD: Int32Array;
	/** profil haut DILATÉ de la marge (peut être négatif), même indexation que botD */
	topD: Int32Array;
	/** rayon de dilatation (cellules) */
	r: number;
	/** bas brut maximal (cellules) — bottom d'un placement = yOff + maxBot */
	maxBot: number;
	/** bbox pivoté en cm (analytique, pour l'affichage) */
	bwCm: number;
	bhCm: number;
}

const maskCanvasCache = new Map<string, HTMLCanvasElement | null>();
const profileCache = new Map<string, Profile>();
const shapeKeyCache = new Map<string, string>();

/** Hash FNV-1a du src COMPLET : deux PNG générés (numéros/textes) partagent
 * en-tête, longueur et fin — un slice tronqué provoquait des collisions de
 * profileCache et donc des silhouettes erronées (logos superposés). */
function shapeKeyOf(src: string): string {
	const cached = shapeKeyCache.get(src);
	if (cached) return cached;
	let h1 = 0x811c9dc5;
	let h2 = 0x811c9dc5;
	for (let i = 0; i < src.length; i++) {
		const ch = src.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 0x01000193) >>> 0;
		h2 = Math.imul(h2 ^ ((ch >> 8) | ((ch & 0xff) << 8)), 0x01000193) >>> 0;
	}
	const key = `${src.length}:${h1.toString(36)}:${h2.toString(36)}`;
	shapeKeyCache.set(src, key);
	return key;
}

function maskCanvas(src: string, mask: ShapeMask | null): HTMLCanvasElement | null {
	if (maskCanvasCache.has(src)) return maskCanvasCache.get(src)!;
	let canvas: HTMLCanvasElement | null = null;
	if (mask) {
		canvas = document.createElement('canvas');
		canvas.width = mask.w;
		canvas.height = mask.h;
		const ctx = canvas.getContext('2d')!;
		const img = ctx.createImageData(mask.w, mask.h);
		for (let i = 0; i < mask.data.length; i++) {
			if (mask.data[i]) img.data[i * 4 + 3] = 255;
		}
		ctx.putImageData(img, 0, 0);
	}
	maskCanvasCache.set(src, canvas);
	return canvas;
}

function buildProfile(
	key: string,
	shape: HTMLCanvasElement | null,
	wCm: number,
	hCm: number,
	angle: number,
	margeCm: number,
): Profile {
	const cached = profileCache.get(key);
	if (cached) return cached;

	const a = rad(angle);
	const cos = Math.abs(Math.cos(a));
	const sin = Math.abs(Math.sin(a));
	const bwCm = wCm * cos + hCm * sin;
	const bhCm = wCm * sin + hCm * cos;
	const c = Math.max(1, Math.ceil(bwCm * RES));
	const rows = Math.max(1, Math.ceil(bhCm * RES));
	const wPx = wCm * RES;
	const hPx = hCm * RES;

	const canvas = document.createElement('canvas');
	canvas.width = c;
	canvas.height = rows;
	const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
	ctx.translate(c / 2, rows / 2);
	ctx.rotate(rad(angle));
	if (shape) {
		ctx.drawImage(shape, -wPx / 2, -hPx / 2, wPx, hPx);
	} else {
		ctx.fillRect(-wPx / 2, -hPx / 2, wPx, hPx);
	}
	const px = ctx.getImageData(0, 0, c, rows).data;

	const top = new Int32Array(c).fill(-1);
	const bot = new Int32Array(c).fill(-1);
	let maxBot = 0;
	for (let y = 0; y < rows; y++) {
		for (let x = 0; x < c; x++) {
			if (px[(y * c + x) * 4 + 3] > COVER_MIN) {
				if (top[x] < 0) top[x] = y;
				bot[x] = y + 1;
				if (y + 1 > maxBot) maxBot = y + 1;
			}
		}
	}

	// dilatation carrée des profils par la marge (garantit margeMm forme-à-forme)
	const r = Math.max(0, Math.ceil(margeCm * RES));
	const botD = new Int32Array(c + 2 * r).fill(-1);
	const topD = new Int32Array(c + 2 * r).fill(-1);
	for (let k = 0; k < c + 2 * r; k++) {
		let v = -1;
		let t = -1;
		const from = Math.max(0, k - 2 * r);
		const to = Math.min(c - 1, k);
		for (let j = from; j <= to; j++) {
			if (bot[j] >= 0 && bot[j] > v) v = bot[j];
			if (top[j] >= 0 && (t < 0 || top[j] < t)) t = top[j];
		}
		if (v >= 0) {
			botD[k] = v + r;
			topD[k] = t - r;
		}
	}

	const profile: Profile = { c, top, bot, botD, topD, r, maxBot, bwCm: round1(bwCm), bhCm: round1(bhCm) };
	profileCache.set(key, profile);
	return profile;
}

interface Instance {
	itemId: string;
	src: string;
	w: number;
	h: number;
	shape: HTMLCanvasElement | null;
	shapeKey: string;
}

interface BoardState {
	sky: Int32Array;
	placements: Placement[];
	bottom: number;
	/** intervalles occupés (dilatés de la marge) par colonne : paires plates [s0,e0,s1,e1,…] triées */
	cols: number[][];
}

/** insère [s,e) dans une liste triée de paires plates, en fusionnant les chevauchements */
function insertInterval(col: number[], s: number, e: number): void {
	let i = 0;
	while (i < col.length && col[i + 1] < s) i += 2;
	let ns = s;
	let ne = e;
	let j = i;
	while (j < col.length && col[j] <= ne) {
		if (col[j] < ns) ns = col[j];
		if (col[j + 1] > ne) ne = col[j + 1];
		j += 2;
	}
	col.splice(i, j - i, ns, ne);
}

/** plus grande borne de fin parmi les intervalles de col intersectant [a,b), ou -1 si libre */
function conflictEnd(col: number[], a: number, b: number): number {
	let best = -1;
	for (let i = 0; i < col.length; i += 2) {
		if (col[i] >= b) break;
		if (col[i + 1] > a && col[i + 1] > best) best = col[i + 1];
	}
	return best;
}

/** commit d'un placement : skyline + intervalles occupés (empreinte dilatée) */
function commitFootprint(board: BoardState, p: Profile, x: number, yOff: number): void {
	const W = board.sky.length;
	for (let k = 0; k < p.c + 2 * p.r; k++) {
		const col = x - p.r + k;
		if (col < 0 || col >= W) continue;
		const v = p.botD[k];
		if (v < 0) continue;
		const abs = yOff + v;
		if (abs > board.sky[col]) board.sky[col] = abs;
		insertInterval(board.cols[col], Math.max(0, yOff + p.topD[k]), abs);
	}
}

/**
 * Cherche un trou DÉJÀ PASSÉ par la skyline (bottom ≤ board.bottom) où la forme brute
 * tient sans toucher les intervalles occupés. Retourne la position de y minimal, ou null.
 */
function findPocket(board: BoardState, p: Profile): { x: number; yOff: number; bottom: number } | null {
	const W = board.sky.length;
	if (p.c > W) return null;
	let maxY = board.bottom - p.maxBot;
	if (maxY < 0) return null;
	let best: { x: number; yOff: number; bottom: number } | null = null;
	for (let x = 0; x <= W - p.c; x++) {
		let y = 0;
		while (y <= maxY) {
			let jump = -1;
			for (let i = 0; i < p.c; i++) {
				const t = p.top[i];
				if (t < 0) continue;
				const e = conflictEnd(board.cols[x + i], y + t, y + p.bot[i]);
				if (e >= 0) {
					const ny = e - t;
					if (ny > jump) jump = ny;
				}
			}
			if (jump < 0) {
				best = { x, yOff: y, bottom: y + p.maxBot };
				maxY = y - 1; // seules des positions strictement plus hautes peuvent battre ce candidat
				break;
			}
			y = Math.max(y + 1, jump);
		}
	}
	return best;
}

/**
 * Placement par silhouette avec rotations. Remplace packItems.
 * Async : l'extraction des masques alpha décode les images.
 */
export async function nestItems(items: DTFItem[], s: DTFSettings): Promise<PackResult> {
	const margeCm = s.margeMm / 10;
	const usableW = s.laizeCm;
	const maxContentH = s.maxPlancheCm;
	const W = Math.floor(usableW * RES);
	const maxCells = Math.floor(maxContentH * RES);

	const rejected: string[] = [];
	const instances: Instance[] = [];
	for (const it of items) {
		if (it.widthCm <= 0 || it.heightCm <= 0 || it.qty <= 0) continue;
		const mask = await getShapeMask(it.src);
		const shape = maskCanvas(it.src, mask);
		const shapeKey = shapeKeyOf(it.src);
		for (let i = 0; i < it.qty; i++) {
			instances.push({ itemId: it.id, src: it.src, w: it.widthCm, h: it.heightCm, shape, shapeKey });
		}
	}

	const step = instances.length > MANY_INSTANCES ? ANGLE_STEP_COARSE : ANGLE_STEP_FINE;
	const angles: number[] = [];
	for (let a = 0; a < 360; a += step) angles.push(a);
	// angle 0 d'abord, puis par écart croissant (tie-break naturel vers l'orientation d'origine)
	angles.sort((x, y) => Math.min(x, 360 - x) - Math.min(y, 360 - y) || x - y);

	// pré-rejet : trop grand sous TOUS les angles
	const fitsSomewhere = (inst: Instance) =>
		angles.some(a => {
			const co = Math.abs(Math.cos(rad(a)));
			const si = Math.abs(Math.sin(rad(a)));
			return inst.w * co + inst.h * si <= usableW + 1e-6 && inst.w * si + inst.h * co <= maxContentH + 1e-6;
		});

	const rejectedIds = new Set<string>();
	let queue = instances.filter(inst => {
		if (fitsSomewhere(inst)) return true;
		rejectedIds.add(inst.itemId);
		return false;
	});
	rejected.push(...rejectedIds);

	queue.sort(
		(a, b) =>
			Math.max(b.w, b.h) - Math.max(a.w, a.h) ||
			b.w * b.h - a.w * a.h ||
			(a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0),
	);

	const boards: BoardState[] = [];
	while (queue.length) {
		const board: BoardState = {
			sky: new Int32Array(W),
			placements: [],
			bottom: 0,
			cols: Array.from({ length: W }, () => [] as number[]),
		};
		const next: Instance[] = [];
		for (const inst of queue) {
			// 1) pose SUR la skyline (chemin rapide)
			let best: { x: number; yOff: number; bottom: number; angle: number; p: Profile } | null = null;
			for (const angle of angles) {
				const key = `${inst.shapeKey}|${inst.w}|${inst.h}|${angle}|${margeCm}`;
				const p = buildProfile(key, inst.shape, inst.w, inst.h, angle, margeCm);
				if (p.c > W) continue;
				for (let x = 0; x <= W - p.c; x++) {
					let yOff = 0;
					for (let i = 0; i < p.c; i++) {
						const t = p.top[i];
						if (t < 0) continue;
						const need = board.sky[x + i] - t;
						if (need > yOff) yOff = need;
					}
					const bottom = yOff + p.maxBot;
					if (bottom > maxCells) continue;
					if (
						!best ||
						bottom < best.bottom ||
						(bottom === best.bottom && (yOff < best.yOff || (yOff === best.yOff && x < best.x)))
					) {
						best = { x, yOff, bottom, angle, p };
					}
				}
			}
			// 2) poches : si rien ne tient sur le dessus, ou si la pose agrandirait la
			// planche, chercher un trou déjà passé (n'agrandit jamais la planche)
			if (!best || best.bottom > board.bottom) {
				let pock: { x: number; yOff: number; bottom: number; angle: number; p: Profile } | null = null;
				for (const angle of angles) {
					const key = `${inst.shapeKey}|${inst.w}|${inst.h}|${angle}|${margeCm}`;
					const p = buildProfile(key, inst.shape, inst.w, inst.h, angle, margeCm);
					if (p.c > W || p.maxBot > board.bottom) continue;
					const f = findPocket(board, p);
					if (f && (!pock || f.bottom < pock.bottom)) pock = { ...f, angle, p };
				}
				if (pock) best = pock;
			}
			if (!best) {
				next.push(inst);
				continue;
			}
			const { x, yOff, p, angle } = best;
			commitFootprint(board, p, x, yOff);
			if (best.bottom > board.bottom) board.bottom = best.bottom;
			board.placements.push({
				itemId: inst.itemId,
				xCm: round1(x * CELL),
				yCm: round1(yOff * CELL),
				wCm: p.bwCm,
				hCm: p.bhCm,
				rotDeg: angle,
			});
		}
		if (!board.placements.length) {
			// aucune instance restante ne rentre même sur planche vide (sécurité)
			for (const inst of next) rejectedIds.add(inst.itemId);
			rejected.length = 0;
			rejected.push(...rejectedIds);
			break;
		}
		boards.push(board);
		queue = next;
	}

	// regroupement des planches identiques : quantité ≠ duplication
	const bySig = new Map<string, Planche>();
	const order: string[] = [];
	for (const board of boards) {
		const contentH = board.bottom * CELL;
		const usedCm = round1(contentH);
		const sig = JSON.stringify(board.placements);
		const found = bySig.get(sig);
		if (found) {
			found.qty += 1;
		} else {
			bySig.set(sig, { placements: board.placements, usedCm, qty: 1 });
			order.push(sig);
		}
	}

	const planches = order.map(sig => bySig.get(sig)!);
	const totalCm = round1(planches.reduce((acc, p) => acc + p.usedCm * p.qty, 0));
	return { planches, totalCm, rejected };
}
